// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

// Components
import DevicesListHeader from "../../../components/UI/DevicesListHeader";
import DevicesCard from "../../../components/UI/DevicesCard";
import Loader from "../../../components/UI/Loader";

// Utilities
import useClinic from "../../../hooks/useClinic";
import { getComparator } from "../../../utils/tableHelper";
import { useDevices } from "../../../hooks/useDevices";
import { useComponentDimensionsById } from "../../../hooks/useComponentDimensionsById";

const DashboardDevicesList = ({
  rows,
  isLoading,
  patients,
  devicesWithExams,
  connection,
}) => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicId, clinicTimezone } = useClinic();

  const {
    unlinkLoading,
    linkLoading,
    unlinkDeviceModal,
    linkDeviceModal,
    unlinkSuccessMessage,
    unlinkFailureMessage,
    linkSuccessMessage,
    linkFailureMessage,
    handleUnlinkDeviceClick,
    handleLinkDeviceClick,
    checkIfDeviceHasExamInProgress,
  } = useDevices(connection, clinicId, patients, devicesWithExams);

  const { dynamicHeight: clinicStatsHeight } = useComponentDimensionsById(
    null,
    "#clinic-stats-container"
  );
  const { dynamicHeight: dashboardExamsHeight } = useComponentDimensionsById(
    null,
    "#dashboard-exams-container"
  );

  // states init
  const [isActive, setIsActive] = useState("all");

  const sortedRows = useMemo(() => {
    // order values
    const order = "asc";
    const orderBy = ["alias"];

    return rows.sort(getComparator(order, orderBy));
  }, [rows]);

  const selectedDevicesRows = useMemo(() => {
    if (isActive === "linked") {
      return sortedRows?.filter((row) => row?.associatedExam);
    } else if (isActive === "unlinked") {
      return sortedRows?.filter((row) => !row?.associatedExam);
    }
    return sortedRows;
  }, [isActive, sortedRows]);

  const listMinHeight = useMemo(() => 420, []);

  const deviceListHeight = useMemo(() => {
    const newHeight = dashboardExamsHeight - clinicStatsHeight - 48;
    return newHeight > listMinHeight ? newHeight : listMinHeight;
  }, [listMinHeight, dashboardExamsHeight, clinicStatsHeight]);

  const ListElement = () =>
    isLoading ? (
      <Loader containerHeight="20vh" />
    ) : (
      <Grid
        container
        gap="20px"
        pb="16px"
        display="flex"
        sx={
          selectedDevicesRows?.length > 1
            ? {
                px: "0px",
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "center" },
                overflowY: { xs: "hidden", md: "auto" },
                overflowX: { xs: "auto", md: "hidden" },
                flexWrap: { xs: "nowrap", md: "wrap" },
                maxHeight: {
                  xs: listMinHeight,
                  md: deviceListHeight,
                },
              }
            : {}
        }
      >
        {selectedDevicesRows?.length > 0 ? (
          selectedDevicesRows?.map((row) => {
            return (
              <Grid item key={row.id}>
                <DevicesCard
                  key={row.id}
                  row={row}
                  handleLinkDeviceClick={handleLinkDeviceClick}
                  handleUnlinkDeviceClick={handleUnlinkDeviceClick}
                  isExamInProgress={checkIfDeviceHasExamInProgress(row.id)}
                  devicesWithExams={devicesWithExams}
                  unlinkLoading={unlinkLoading}
                  linkLoading={linkLoading}
                  clinicTimezone={clinicTimezone}
                />
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box my={8} display="flex" justifyContent="center">
              <Typography sx={{ textTransform: "capitalize" }}>
                {t("word_no_devices")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    );

  return (
    <>
      {rows?.length > 0 && (
        <DevicesListHeader
          rows={rows}
          isActive={isActive}
          setIsActive={setIsActive}
          containerProps={{ px: "0px", py: "0px" }}
        />
      )}

      {ListElement()}

      {/* Unlink device exam modal */}
      {unlinkDeviceModal}

      {/* Link device exam modal */}
      {linkDeviceModal}
      {/* Expand devices modal */}

      {/* Unlink device success/failure toasts */}
      {unlinkSuccessMessage}
      {unlinkFailureMessage}
      {linkSuccessMessage}
      {linkFailureMessage}
    </>
  );
};

export default DashboardDevicesList;
